/**
 * O portão de identidade do inscrito — autocadastro e recuperação de senha.
 *
 * CPF (ou gov.br) → canal → código → senha, com a API de F04 decidindo cada
 * passo (`maquinaDeIdentidade.ts` guarda só o que é da tela). No fim, o
 * inscrito sai com a sessão aberta — um cookie `HttpOnly` que a tela nunca vê
 * — e `aoConfirmar` avisa o pai uma única vez.
 *
 * **Campo de senha, por decisão do dono (D-10, CLAUDE.md, 2026-09-28).** A
 * conta do inscrito vive fora do Keycloak e só serve para acompanhar a
 * inscrição; o §7 de `docs/AUTENTICACAO-E-ACESSO.md` foi contrariado de forma
 * consciente. Senha só aparece aqui, no entrar e no recuperar.
 */

import {
  AlertTriangle,
  ArrowLeft,
  Building2,
  Clock,
  KeyRound,
  Lock,
  ShieldCheck,
} from 'lucide-react';
import { useCallback, useEffect, useMemo, useReducer, useRef, type ReactNode } from 'react';

import { ErroApi } from '@/nucleo/auth/cliente-api';
import { BotaoGovBr } from '@/ui/BotaoGovBr';
import { Alert, AlertDescription, AlertTitle } from '@/ui/base/alert';
import { Badge } from '@/ui/base/badge';
import { Button } from '@/ui/base/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/ui/base/card';

import { ETAPAS_DO_AUTOCADASTRO, TEMPO_ESTIMADO_DO_AUTOCADASTRO } from '../contrato';
import { apiDoInscrito, type FinalidadeDoFluxo } from './api';
import { IdentidadeCanal } from './IdentidadeCanal';
import { IdentidadeCodigo } from './IdentidadeCodigo';
import { IdentidadeCpf } from './IdentidadeCpf';
import { IdentidadeGovbr } from './IdentidadeGovbr';
import { IdentidadeSenha } from './IdentidadeSenha';
import {
  AVISO_DO_ENVIO,
  PASSOS_DA_IDENTIDADE,
  avisoDaConferencia,
  estadoInicialDaIdentidade,
  reduzirIdentidade,
  viaDaIdentidade,
  type AcaoDaIdentidade,
  type AcoesDoPortao,
  type EstadoDaIdentidade,
  type MotivoDeOrientacao,
  type PassoDaIdentidade,
  type ViaDaIdentidade,
} from './maquinaDeIdentidade';

export interface IdentidadeDoAutocadastro {
  via: ViaDaIdentidade;
  /** Só dígitos; vazio quando a pessoa entrou pelo gov.br sem digitar o CPF. */
  cpf: string;
  nome: string | null;
}

export interface PortaoDeIdentidadeProps {
  /** `CADASTRO` no autocadastro; `RECUPERACAO` em "Esqueci minha senha". */
  finalidade?: FinalidadeDoFluxo;
  /** Chamado uma única vez, quando a sessão do inscrito foi aberta. */
  aoConfirmar: (identidade: IdentidadeDoAutocadastro) => void;
}

const CABECALHO: Record<PassoDaIdentidade, { titulo: string; anterior: PassoDaIdentidade | null }> =
  {
    cpf: { titulo: 'Identifique-se', anterior: null },
    canal: { titulo: 'Canal de verificação', anterior: 'cpf' },
    codigo: { titulo: 'Código de verificação', anterior: 'canal' },
    senha: { titulo: 'Senha de acesso', anterior: null },
    govbr: { titulo: 'Confirmação pelo gov.br', anterior: 'cpf' },
  };

const DESCRICAO: Record<FinalidadeDoFluxo, Record<PassoDaIdentidade, string>> = {
  CADASTRO: {
    cpf: 'Para começar, vamos confirmar quem é você usando o contato cadastrado no DTP.',
    canal: 'Escolha por onde o código de 6 dígitos deve chegar.',
    codigo: 'Digite o código que enviamos para o contato escolhido.',
    senha: 'Identidade confirmada. Falta a senha com que você volta depois.',
    govbr: 'Confirme a sua identidade no gov.br e volte a esta página.',
  },
  RECUPERACAO: {
    cpf: 'Informe o seu CPF. O código para redefinir a senha vai para o contato cadastrado no DTP.',
    canal: 'Escolha por onde o código de 6 dígitos deve chegar.',
    codigo: 'Digite o código que enviamos para o contato escolhido.',
    senha: 'Identidade confirmada. Escolha a nova senha.',
    govbr: 'Confirme a sua identidade no gov.br e volte a esta página.',
  },
};

/**
 * O que dizer em cada beco sem saída. Sem endereço nem telefone do DTP: o
 * contato do atendimento vem de parâmetro do banco, e escrevê-lo aqui seria
 * dado inventado.
 */
const ORIENTACOES: Record<MotivoDeOrientacao, { titulo: string; texto: string; dtp: boolean }> = {
  PROCURAR_DTP: {
    titulo: 'Não foi possível seguir com este CPF',
    texto:
      'Confira o número digitado. Se ele estiver certo, procure o Departamento de Transportes Públicos (DTP) com documento oficial com foto para regularizar o cadastro de transportador e os contatos dele.',
    dtp: true,
  },
  EMAIL_DESATUALIZADO: {
    titulo: 'O e-mail cadastrado parece desatualizado',
    texto:
      'O código só vai para o e-mail que está no cadastro do DTP. Se ele não é mais seu, escolha outro canal ou procure o DTP para atualizar.',
    dtp: true,
  },
  TELEFONE_DESATUALIZADO: {
    titulo: 'O celular cadastrado parece desatualizado',
    texto:
      'O código por SMS só vai para o celular que está no cadastro do DTP. Se ele não é mais seu, escolha outro canal ou procure o DTP para atualizar.',
    dtp: true,
  },
  /*
   * Falha técnica e dado ausente são coisas diferentes: sistema fora do ar é
   * passageiro e se resolve tentando de novo, sem ir ao balcão.
   */
  SGTP_INDISPONIVEL: {
    titulo: 'Não foi possível consultar o cadastro agora',
    texto:
      'A consulta ao cadastro de transportadores não respondeu. Costuma ser passageiro: espere alguns minutos e tente de novo.',
    dtp: false,
  },
  GOVBR_RECUSADO: {
    titulo: 'O gov.br não confirmou a sua identidade',
    texto:
      'A conta gov.br precisa ter selo prata ou ouro. Você pode aumentar o nível da conta no gov.br ou seguir pelo CPF, com o código enviado ao contato cadastrado no DTP.',
    dtp: false,
  },
  GOVBR_INDISPONIVEL: {
    titulo: 'O gov.br não respondeu agora',
    texto: 'Tente de novo em alguns minutos, ou siga pelo CPF com o código de verificação.',
    dtp: false,
  },
};

/** A frase do erro da API, ou a genérica. Nunca o detalhe técnico. */
function fraseDoErro(erro: unknown): string {
  return erro instanceof ErroApi
    ? erro.aviso
    : 'Não foi possível concluir. Tente de novo em instantes.';
}

export function PortaoDeIdentidade({
  finalidade = 'CADASTRO',
  aoConfirmar,
}: PortaoDeIdentidadeProps) {
  const [estado, despachar] = useReducer(reduzirIdentidade, Date.now(), estadoInicialDaIdentidade);

  // O relógio só bate enquanto há código contando.
  const contando = estado.fluxo?.codigo != null && estado.passo === 'codigo';
  useEffect(() => {
    if (!contando) return;
    const id = window.setInterval(() => despachar({ tipo: 'tique', agora: Date.now() }), 1000);
    return () => window.clearInterval(id);
  }, [contando]);

  // `aoConfirmar` avisa UMA vez, mesmo que o pai troque a função.
  const avisar = useRef(aoConfirmar);
  useEffect(() => {
    avisar.current = aoConfirmar;
  }, [aoConfirmar]);
  const jaConfirmou = useRef(false);

  // O estado mais recente, para as ações lerem sem se recriarem a cada tique.
  const atual = useRef(estado);
  atual.current = estado;

  const concluir = useCallback((nome: string | null) => {
    if (jaConfirmou.current) return;
    jaConfirmou.current = true;
    const e = atual.current;
    avisar.current({ via: viaDaIdentidade(e) ?? e.canal, cpf: e.cpf, nome });
  }, []);

  /**
   * Toda chamada passa por aqui: marca ocupado, e erro vira frase. 401 quer
   * dizer que o fluxo venceu ou já foi usado — volta ao começo.
   */
  const executar = useCallback(async (trecho: () => Promise<void>) => {
    despachar({ tipo: 'ocupar' });
    try {
      await trecho();
    } catch (erro) {
      if (erro instanceof ErroApi && erro.situacao === 401) {
        despachar({ tipo: 'ir_para', passo: 'cpf' });
      }
      despachar({ tipo: 'avisar', aviso: fraseDoErro(erro) });
    }
  }, []);

  const acoes: AcoesDoPortao = useMemo(
    () => ({
      continuarComCpf: () =>
        executar(async () => {
          const r = await apiDoInscrito.abrirFluxo(atual.current.cpf, finalidade);
          if (r.resultado === 'CONTATOS') {
            despachar({ tipo: 'receber_fluxo', fluxo: r.fluxo, agora: Date.now() });
          } else {
            despachar({
              tipo: 'orientar',
              motivo: r.resultado === 'PROCURAR_DTP' ? 'PROCURAR_DTP' : 'SGTP_INDISPONIVEL',
            });
          }
        }),
      enviarCodigo: () =>
        executar(async () => {
          const r = await apiDoInscrito.enviarCodigo(atual.current.canal);
          despachar({
            tipo: 'receber_fluxo',
            fluxo: r.fluxo,
            agora: Date.now(),
            aviso: AVISO_DO_ENVIO[r.resultado] ?? null,
            passo: r.resultado === 'CANAL_INDISPONIVEL' ? 'canal' : undefined,
          });
        }),
      conferirCodigo: () =>
        executar(async () => {
          const r = await apiDoInscrito.conferirCodigo(atual.current.codigo);
          despachar({
            tipo: 'receber_fluxo',
            fluxo: r.fluxo,
            agora: Date.now(),
            aviso: avisoDaConferencia(r.resultado, r.fluxo.codigo?.tentativasRestantes ?? 0),
          });
        }),
      definirSenha: async (senha, confirmacao) => {
        let faltam: string[] | 'DIFERENTE' | null = null;
        await executar(async () => {
          const r = await apiDoInscrito.definirSenha(senha, confirmacao);
          if (r.resultado === 'DEFINIDA') {
            despachar({ tipo: 'avisar', aviso: null });
            concluir(r.inscrito.nome);
            return;
          }
          faltam = r.resultado === 'SENHA_FRACA' ? r.requisitos : 'DIFERENTE';
          despachar({ tipo: 'avisar', aviso: null });
        });
        return faltam;
      },
      entrarSemTrocarSenha: () =>
        executar(async () => {
          const r = await apiDoInscrito.entrarPeloFluxo();
          despachar({ tipo: 'avisar', aviso: null });
          concluir(r.inscrito.nome);
        }),
      iniciarGovbr: () =>
        executar(async () => {
          const r = await apiDoInscrito.iniciarGovbr();
          if (r.resultado === 'PENDENTE') despachar({ tipo: 'aguardar_govbr' });
          else despachar({ tipo: 'orientar', motivo: 'GOVBR_INDISPONIVEL' });
        }),
      confirmarGovbr: () =>
        executar(async () => {
          const r = await apiDoInscrito.confirmarGovbr();
          switch (r.resultado) {
            case 'CONFIRMADO':
              despachar({ tipo: 'receber_fluxo', fluxo: r.fluxo, agora: Date.now() });
              return;
            case 'PENDENTE':
              despachar({
                tipo: 'avisar',
                aviso: 'Ainda não recebemos a confirmação do gov.br. Conclua lá e tente de novo.',
              });
              return;
            case 'RECUSADO':
              despachar({ tipo: 'orientar', motivo: 'GOVBR_RECUSADO' });
              return;
            case 'PROCURAR_DTP':
              despachar({ tipo: 'orientar', motivo: 'PROCURAR_DTP' });
              return;
            case 'SGTP_INDISPONIVEL':
              despachar({ tipo: 'orientar', motivo: 'SGTP_INDISPONIVEL' });
              return;
            default:
              despachar({ tipo: 'orientar', motivo: 'GOVBR_INDISPONIVEL' });
          }
        }),
    }),
    [concluir, executar, finalidade],
  );

  const { titulo, anterior } = CABECALHO[estado.passo];
  const posicao = (PASSOS_DA_IDENTIDADE as readonly string[]).indexOf(estado.passo) + 1;

  return (
    <div className="grid gap-8 lg:grid-cols-[1fr_minmax(0,440px)] lg:items-start lg:gap-12">
      {finalidade === 'CADASTRO' ? <ContextoDaInscricao /> : <ContextoDaRecuperacao />}
      <Card className="w-full">
        <CardHeader className="space-y-3">
          <div className="flex flex-wrap items-center justify-between gap-2">
            {anterior && !estado.orientacao ? (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="h-8 px-2 text-xs text-muted-foreground"
                onClick={() => despachar({ tipo: 'ir_para', passo: anterior })}
              >
                <ArrowLeft aria-hidden="true" />
                Voltar
              </Button>
            ) : (
              <Badge variant="outline" className="gap-1.5 border-primary/30">
                <Lock aria-hidden="true" className="h-3.5 w-3.5" />
                {finalidade === 'CADASTRO' ? 'Primeiro acesso' : 'Redefinir senha'}
              </Badge>
            )}
            {/* A senha é o desfecho da verificação, não mais um passo dela. */}
            {posicao >= 1 && posicao <= 3 && (
              <p className="text-xs font-medium text-muted-foreground">
                Passo {posicao} de {PASSOS_DA_IDENTIDADE.length - 1}
              </p>
            )}
          </div>
          <div className="space-y-1">
            <CardTitle className="text-lg">{titulo}</CardTitle>
            <CardDescription>{DESCRICAO[finalidade][estado.passo]}</CardDescription>
          </div>
        </CardHeader>

        <CardContent className="space-y-4">
          {estado.orientacao ? (
            <AlertaDeOrientacao
              motivo={estado.orientacao}
              ocupado={estado.ocupado}
              aoVoltar={() => despachar({ tipo: 'dispensar_orientacao' })}
              aoUsarGovBr={acoes.iniciarGovbr}
            />
          ) : (
            <PainelDoPasso passo={estado.passo}>
              <PassoAtual
                estado={estado}
                despachar={despachar}
                acoes={acoes}
                finalidade={finalidade}
              />
            </PainelDoPasso>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function PassoAtual({
  estado,
  despachar,
  acoes,
  finalidade,
}: {
  estado: EstadoDaIdentidade;
  despachar: (acao: AcaoDaIdentidade) => void;
  acoes: AcoesDoPortao;
  finalidade: FinalidadeDoFluxo;
}) {
  const props = { estado, despachar, acoes };
  switch (estado.passo) {
    case 'cpf':
      return <IdentidadeCpf {...props} />;
    case 'canal':
      return <IdentidadeCanal {...props} />;
    case 'codigo':
      return <IdentidadeCodigo {...props} />;
    case 'govbr':
      return <IdentidadeGovbr {...props} />;
    case 'senha':
      return <IdentidadeSenha {...props} finalidade={finalidade} />;
    default:
      return null;
  }
}

/**
 * A coluna da esquerda do autocadastro: o que a pessoa vai preencher depois de
 * comprovar quem é. As quatro etapas aparecem com cadeado — são o destino.
 */
function ContextoDaInscricao() {
  return (
    <aside className="space-y-6">
      <div className="space-y-2">
        <Badge variant="outline" className="gap-1.5 border-primary/30">
          <Lock aria-hidden="true" className="h-3.5 w-3.5" />
          Inscrição · Primeiro acesso
        </Badge>
        <h1 className="text-3xl font-bold leading-tight text-foreground lg:text-4xl lg:leading-10">
          Inscrição no Credenciamento TEG
        </h1>
        <p className="text-sm leading-relaxed text-muted-foreground sm:text-base sm:leading-6">
          Para iniciar o preenchimento, comprove sua identidade. Nenhum campo é salvo antes da
          verificação.
        </p>
      </div>

      <div className="space-y-3">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <p className="text-sm font-semibold text-foreground">
            Depois da verificação, você preenche estas 4 etapas:
          </p>
          <Badge variant="outline" className="gap-1.5 text-xs">
            <Clock aria-hidden="true" className="h-3.5 w-3.5" />
            {TEMPO_ESTIMADO_DO_AUTOCADASTRO}
          </Badge>
        </div>

        <ol className="space-y-2">
          {ETAPAS_DO_AUTOCADASTRO.map(({ numero, rotulo, descricao, icone: Icone }) => (
            <li
              key={numero}
              className="flex items-center gap-3 rounded-xl border border-border bg-muted/30 p-3"
            >
              <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-background text-muted-foreground">
                <Icone aria-hidden="true" className="h-5 w-5" />
              </span>
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium text-foreground">
                  <span className="mr-1.5 font-mono text-xs text-muted-foreground">
                    {String(numero).padStart(2, '0')}
                  </span>
                  {rotulo}
                </p>
                <p className="text-xs text-muted-foreground">{descricao}</p>
              </div>
              <Lock aria-hidden="true" className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
            </li>
          ))}
        </ol>
      </div>

      <p className="flex gap-2 rounded-xl border border-dashed border-border bg-muted/30 p-3 text-xs leading-relaxed text-muted-foreground">
        <ShieldCheck aria-hidden="true" className="mt-0.5 h-4 w-4 shrink-0 text-success" />
        <span>
          Nenhum dado pessoal é gravado antes da verificação de identidade. A sessão termina quando
          você fecha o navegador — em computador público, feche ao terminar.
        </span>
      </p>
    </aside>
  );
}

function ContextoDaRecuperacao() {
  return (
    <aside className="space-y-4">
      <Badge variant="outline" className="gap-1.5 border-primary/30">
        <KeyRound aria-hidden="true" className="h-3.5 w-3.5" />
        Acompanhar inscrição
      </Badge>
      <h1 className="text-3xl font-bold leading-tight text-foreground lg:text-4xl lg:leading-10">
        Redefinir a senha
      </h1>
      <p className="text-sm leading-relaxed text-muted-foreground sm:text-base sm:leading-6">
        Para trocar a senha com que você acompanha a inscrição, confirme a sua identidade com um
        código enviado ao contato cadastrado no DTP, ou pelo gov.br.
      </p>
      <p className="flex gap-2 rounded-xl border border-dashed border-border bg-muted/30 p-3 text-xs leading-relaxed text-muted-foreground">
        <ShieldCheck aria-hidden="true" className="mt-0.5 h-4 w-4 shrink-0 text-success" />
        <span>
          Ao redefinir a senha, as outras sessões abertas com o seu CPF são encerradas, e avisamos
          você pelo mesmo contato.
        </span>
      </p>
    </aside>
  );
}

/** Uma região viva por passo: quem usa leitor de tela ouve a troca. */
function PainelDoPasso({ passo, children }: { passo: PassoDaIdentidade; children: ReactNode }) {
  return (
    <div role="group" aria-live="polite" aria-label={CABECALHO[passo].titulo}>
      {children}
    </div>
  );
}

function AlertaDeOrientacao({
  motivo,
  ocupado,
  aoVoltar,
  aoUsarGovBr,
}: {
  motivo: MotivoDeOrientacao;
  ocupado: boolean;
  aoVoltar: () => void;
  aoUsarGovBr: () => void;
}) {
  const { titulo, texto, dtp } = ORIENTACOES[motivo];
  const ofereceGovbr = motivo !== 'GOVBR_INDISPONIVEL' && motivo !== 'GOVBR_RECUSADO';
  return (
    <div className="space-y-3">
      <Alert className="border-warning/40 bg-warning/10">
        <AlertTriangle aria-hidden="true" className="h-4 w-4" />
        <AlertTitle className="flex flex-wrap items-center gap-2">
          {titulo}
          {dtp && (
            <Badge variant="outline" className="gap-1 border-warning/40 text-xs">
              <Building2 aria-hidden="true" className="h-3 w-3" />
              Regularização no DTP
            </Badge>
          )}
        </AlertTitle>
        <AlertDescription className="space-y-3">
          <p>{texto}</p>
          <Button type="button" variant="outline" size="sm" onClick={aoVoltar}>
            <ArrowLeft aria-hidden="true" />
            Voltar
          </Button>
        </AlertDescription>
      </Alert>

      {ofereceGovbr && (
        <div className="space-y-2 rounded-xl border border-dashed border-border bg-muted/30 p-3">
          <p className="text-xs text-muted-foreground">
            Você também pode confirmar a identidade pelo gov.br. O CPF confirmado lá precisa estar
            no cadastro do DTP.
          </p>
          <BotaoGovBr onClick={aoUsarGovBr} disabled={ocupado}>
            Entrar com gov.br
          </BotaoGovBr>
        </div>
      )}
    </div>
  );
}

export default PortaoDeIdentidade;
